import React, { useCallback, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import { useAuth } from '../../contexts/AuthContext';
import { canView } from '../../utils/permissions';
import { LopAgGrid } from '../../components/LopAgGrid';
import { parseDevToolsFile } from '../../api/devTools';
import type { DevToolsFileType, ParsedFileResponse } from '../../types/DevTools';
import { t } from '../../i18n/t';
import { LopButton } from '../../components/LopButton';
import { LopFilePicker } from '../../components/LopFilePicker';
import './ParseFilesPage.css';

type Row = Record<string, string | null>;

// Dropdown order per the backend's SupportedFileTypes dispatch table.
const FILE_TYPES: DevToolsFileType[] = [
  'poalim',
  'leumi',
  'mizrahi',
  'discount-yit',
  'discount-yth',
  'discount-dtak',
];

function fileTypeLabel(ft: DevToolsFileType): string {
  switch (ft) {
    case 'poalim':
      return t('devTools.parseFiles.fileTypes.poalim');
    case 'leumi':
      return t('devTools.parseFiles.fileTypes.leumi');
    case 'mizrahi':
      return t('devTools.parseFiles.fileTypes.mizrahi');
    case 'discount-yit':
      return t('devTools.parseFiles.fileTypes.discountYit');
    case 'discount-yth':
      return t('devTools.parseFiles.fileTypes.discountYth');
    case 'discount-dtak':
      return t('devTools.parseFiles.fileTypes.discountDtak');
    default:
      return ft;
  }
}

// "פרסור קבצים" — the first tool under the SysAdmin-only "פיתוח והטמעה" toolbox.
// Upload a raw bank file, run it through its existing adapter (server-side,
// read-only — no ingestion), and preview the parsed rows in a grid. Every bank
// format has a different column set, so the grid's columns are built
// dynamically from the response rather than a fixed ColDef list.
export function ParseFilesPage() {
  const { user } = useAuth();

  const [fileType, setFileType] = useState<DevToolsFileType>('poalim');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ParsedFileResponse | null>(null);

  const canRun = file !== null;

  const handleParse = useCallback(async () => {
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const outcome = await parseDevToolsFile(fileType, file);
      if (outcome.ok) {
        setResult(outcome.result);
      } else {
        setError(outcome.error);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [fileType, file]);

  // Dynamic columns built from the response's own column list — the whole
  // point of this screen is that every bank format has a different shape.
  const columnDefs = useMemo<ColDef<Row>[]>(() => {
    if (!result) return [];
    return result.columns.map((col) => ({
      field: col,
      headerName: col,
      minWidth: 120,
    }));
  }, [result]);

  const rowData = useMemo<Row[]>(() => result?.rows ?? [], [result]);

  if (!canView(user, 'devTools')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="devtools-parse">
      <h1 className="devtools-parse__title">{t('devTools.parseFiles.pageTitle')}</h1>
      <p className="devtools-parse__subtitle">{t('devTools.parseFiles.subtitle')}</p>

      <section className="devtools-parse__panel">
        <div className="devtools-parse__field">
          <label className="devtools-parse__label" htmlFor="devtools-parse-filetype">
            {t('devTools.parseFiles.fileTypeLabel')}
          </label>
          <select
            id="devtools-parse-filetype"
            className="devtools-parse__select"
            value={fileType}
            onChange={(e) => setFileType(e.target.value as DevToolsFileType)}
          >
            {FILE_TYPES.map((ft) => (
              <option key={ft} value={ft}>
                {fileTypeLabel(ft)}
              </option>
            ))}
          </select>
        </div>

        <div className="devtools-parse__field">
          <label className="devtools-parse__label" htmlFor="devtools-parse-file">
            {t('devTools.parseFiles.fileLabel')}
          </label>
          <LopFilePicker
            id="devtools-parse-file"
            files={file ? [file] : []}
            onChange={(files) => setFile(files[0] ?? null)}
          />
        </div>

        <div className="devtools-parse__actions">
          <LopButton variant="primary" disabled={!canRun} onClick={handleParse}>
            {t('devTools.parseFiles.parseButton')}
          </LopButton>
        </div>

        {error !== null && (
          <div className="devtools-parse__result devtools-parse__result--error" role="alert">
            {error}
          </div>
        )}
      </section>

      {result && (
        <section className="devtools-parse__grid-section">
          <div className="devtools-parse__row-count">
            {t('devTools.parseFiles.rowCount', { count: result.rowCount })}
          </div>
          <div className="devtools-parse__grid">
            <LopAgGrid<Row> rowData={rowData} columnDefs={columnDefs} />
          </div>
        </section>
      )}
    </div>
  );
}
